// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Client-side theme preference handling.
 *
 * - Preference is stored in localStorage + cookie (sb_theme)
 * - SSR reads the cookie and sets <html data-sb-theme="..."> so the initial render matches.
 * - The default is "auto" (= follow prefers-color-scheme).
 */
import type { InitialState, ThemeChoice } from "../../shared/src/types";
import type { Translator } from "@unilarva/translator";

const THEME_COOKIE = "sb_theme";
const THEME_STORAGE_KEY = "sb_theme";

function isThemeChoice(x: unknown): x is ThemeChoice {
  return x === "auto" || x === "light" || x === "dark";
}

export class ThemeManager {
  private readonly state: InitialState;
  private readonly translator: Translator;

  private readonly prefersDarkMql = window.matchMedia("(prefers-color-scheme: dark)");

  constructor(state: InitialState, translator: Translator) {
    this.state = state;
    this.translator = translator;

    // Best-effort: ensure documentElement has a theme attribute early.
    this.applyPersistedThemePreference();
  }

  init(): void {
    document.addEventListener("click", this.handleThemeChoiceClick, { capture: true });

    // Keep the "Auto (Light/Dark)" label updated when:
    // - the language changes
    // - the browser/OS preference changes
    this.translator.subscribe(() => this.refreshThemeLabel());

    const handlePrefersChange = () => {
      // Update the Auto(...) label even if the active theme is not "auto".
      this.refreshThemeLabel();
    };

    // matchMedia change events: modern + fallback.
    if (typeof this.prefersDarkMql.addEventListener === "function") {
      this.prefersDarkMql.addEventListener("change", handlePrefersChange);
    } else {
      // eslint-disable-next-line deprecation/deprecation
      this.prefersDarkMql.addListener(handlePrefersChange);
    }

    this.refreshThemeLabel();
  }

  /** Applies theme from localStorage or cookie if available. Defaults to 'auto'. */
  private applyPersistedThemePreference(): void {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (isThemeChoice(stored)) {
      this.setTheme(stored, { persist: false });
      return;
    }

    const cookieTheme = this.readCookieTheme();
    if (isThemeChoice(cookieTheme)) {
      // SSR should already have applied it, but keep state in sync.
      this.state.theme = cookieTheme;
      this.ensureHtmlThemeAttr(cookieTheme);
      return;
    }

    // No preference stored: follow browser default.
    this.setTheme("auto", { persist: false });
  }

  private ensureHtmlThemeAttr(theme: ThemeChoice): void {
    const root = document.documentElement;
    if (root.dataset.sbTheme !== theme) root.dataset.sbTheme = theme;
  }

  private handleThemeChoiceClick = (e: MouseEvent): void => {
    const target = e.target as HTMLElement | null;
    if (!target) return;

    const btn = target.closest<HTMLElement>("[data-sb-theme-choice]");
    if (!btn) return;

    const choiceRaw = (btn.getAttribute("data-sb-theme-choice") || "").trim();
    if (!isThemeChoice(choiceRaw)) return;

    e.preventDefault();
    this.setTheme(choiceRaw, { persist: true });
  };

  private setTheme(theme: ThemeChoice, opts: { persist: boolean }): void {
    this.state.theme = theme;
    this.ensureHtmlThemeAttr(theme);

    if (opts.persist) {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
      document.cookie = `${THEME_COOKIE}=${encodeURIComponent(theme)}; Path=/; Max-Age=31536000; SameSite=Lax`;
    }

    this.refreshThemeLabel();
  }

  private setIcon(el: Element | null | undefined, which: "sun" | "moon"): void {
    if (!el) return;
    el.classList.remove("sbi-sun", "sbi-moon");
    el.classList.add(which === "moon" ? "sbi-moon" : "sbi-sun");
  }

  private refreshThemeLabel(): void {
    const prefersDark = this.prefersDarkMql.matches;
    const resolvedWhich: "sun" | "moon" = prefersDark ? "moon" : "sun";
    const theme = this.state.theme;
    const themeMenu = document.querySelector<HTMLElement>(".sb-side-theme");

    themeMenu?.querySelectorAll<HTMLElement>("[data-sb-theme-choice]").forEach(choice => {
      const selected = choice.getAttribute("data-sb-theme-choice") === theme;
      if (selected) choice.setAttribute("aria-current", "true");
      else choice.removeAttribute("aria-current");
    });

    const autoLabel = this.translator.translateKey("theme.auto");
    const modeLabel = this.translator.translateKey(prefersDark ? "theme.dark" : "theme.light");
    const autoText = `${autoLabel} (${modeLabel})`;

    // ── Dropdown item: Auto ────────────────────────────────────────────
    const autoChoiceBtn = themeMenu?.querySelector<HTMLElement>('[data-sb-theme-choice="auto"]');
    const autoChoiceText = themeMenu?.querySelector<HTMLElement>(
      '[data-sb-theme-choice-text="auto"]',
    );
    const autoChoiceIcon = themeMenu?.querySelector<HTMLElement>(
      '[data-sb-theme-choice-icon="auto"]',
    );

    if (autoChoiceBtn) {
      autoChoiceBtn.setAttribute("aria-label", autoText);
      autoChoiceBtn.setAttribute("title", autoText);
      autoChoiceBtn.classList.toggle("sb-theme-auto--dark", prefersDark);
      autoChoiceBtn.classList.toggle("sb-theme-auto--light", !prefersDark);
    }

    if (autoChoiceText) autoChoiceText.textContent = autoText;
    this.setIcon(autoChoiceIcon, resolvedWhich);

    // ── Current selection label (in summary) ───────────────────────────
    const currentTextEl = themeMenu?.querySelector<HTMLElement>("[data-sb-theme-current-text]");
    const currentIconEl = themeMenu?.querySelector<HTMLElement>("[data-sb-theme-current-icon]");

    if (!currentTextEl) return;

    if (theme === "light" || theme === "dark") {
      const key = theme === "dark" ? "theme.dark" : "theme.light";
      currentTextEl.textContent = this.translator.translateKey(key);
      this.setIcon(currentIconEl, theme === "dark" ? "moon" : "sun");
      return;
    }

    // theme === 'auto'
    currentTextEl.textContent = autoText;
    this.setIcon(currentIconEl, resolvedWhich);
  }

  private readCookieTheme(): ThemeChoice | null {
    const cookie = typeof document === "undefined" ? "" : document.cookie || "";
    if (!cookie) return null;

    for (const part of cookie.split(";")) {
      const [k, ...rest] = part.trim().split("=");
      if (k !== THEME_COOKIE) continue;
      const v = decodeURIComponent(rest.join("=") || "").trim();
      return isThemeChoice(v) ? v : null;
    }

    return null;
  }
}
